<template>
  <div :class="containerClass">
    <!-- New Ingredient Input Row -->
    <div v-if="isNewIngredient" class="ingredient-new">
      <button class="add-row-btn" @click="add">
        <b-icon icon="plus" size="is-small" />
        Add ingredient
      </button>
    </div>

    <!-- Existing Ingredient -->
    <template v-else>
      <!-- Top row: drag handle, optional toggle, delete button -->
      <div class="ingredient-header">
        <b-icon
          class="drag-handle ingredient-drag"
          icon="drag"
          size="is-small"
        />
        <div class="ingredient-header-controls">
          <label class="optional-label">
            <span>Optional</span>
            <div
              class="ingredient-toggle"
              :class="{ active: ingredient.optional }"
              :title="ingredient.optional ? 'Optional ingredient' : 'Required ingredient'"
              @click="toggleOptional">
            </div>
          </label>
          <button class="ingredient-action-btn" title="Delete" @click="remove">
            <b-icon icon="delete" size="is-small" />
          </button>
        </div>
      </div>

      <!-- Input fields row -->
      <div class="ingredient-row">
        <b-input
          class="ingredient-qty"
          type="number"
          step="0.05"
          v-model.number="ingredient.amount"
          placeholder="Qty"
          @input="update()">
        </b-input>
        <b-autocomplete
          class="ingredient-unit"
          v-model.trim="ingredient.unit"
          ref="unitsAutocomplete"
          field="term"
          placeholder="Unit"
          :data="filteredUnits"
          open-on-focus
          @select="option => selectedUnit(option)">
          <template #footer>
            <a @click="openNewUnitModal(ingredient.unit)">
              <span>+ Add new</span>
            </a>
          </template>
          <template #empty>
            <span><strong>{{ ingredient.unit }}</strong> not found!</span>
          </template>
        </b-autocomplete>
        <b-autocomplete
          class="ingredient-name"
          v-model.trim="ingredient.name"
          ref="nameAutocomplete"
          field="name"
          placeholder="Ingredient name"
          :data="filteredIngredients"
          open-on-focus
          @select="option => selectedIngredient(option)"
          @blur="onNameBlur">
          <template #footer>
            <a @click="openNewIngredientModal(ingredient.name)">
              <span>+ Add new</span>
            </a>
          </template>
          <template #empty>
            <span><strong>{{ingredient.name}}</strong> not found!</span>
          </template>
        </b-autocomplete>
        <b-autocomplete
          class="ingredient-prep"
          v-model.trim="ingredient.prep"
          ref="prepAutocomplete"
          field="term"
          placeholder="Prep..."
          :data="filteredPreps"
          open-on-focus
          @select="option => selectedPrep(option)">
          <template #footer>
            <a @click="openNewPrepModal(ingredient.prep)">
              <span>+ Add new</span>
            </a>
          </template>
          <template #empty>
            <span><strong>{{ingredient.prep}}</strong> not found!</span>
          </template>
        </b-autocomplete>
      </div>

      <!-- Tips Section -->
      <div class="ingredient-tips">
        <draggable
          v-if="ingredient.tips && ingredient.tips.length > 0"
          v-model="ingredient.tips"
          draggable=".ingredient-tip-item"
          handle=".tip-drag-handle"
          ghostClass="drag-ghost"
          dragClass="drag-item"
          @update="update">
          <div v-for="(tip, idx) in ingredient.tips" :key="tip.id" class="ingredient-tip-item">
            <div class="ingredient-tip-wrapper">
              <svg class="tip-drag-handle ingredient-tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
              <editor
                v-model.trim="tip.value"
                class="ingredient-tip-input"
                placeholder="Add a tip for this ingredient..."
                @input="update"
              />
              <button class="tip-remove-btn" @click="removeTip(tip, idx)">
                <b-icon icon="delete" size="is-small" />
              </button>
            </div>
          </div>
        </draggable>

        <!-- Always visible new tip input -->
        <div class="ingredient-tip-item new-tip">
          <div class="ingredient-tip-wrapper add-new">
            <svg class="ingredient-tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
            </svg>
            <input
              type="text"
              v-model.trim="newTipValue"
              class="new-tip-input"
              placeholder="Add a tip..."
              @keyup.enter="addTipFromInput"
            />
            <button
              v-if="newTipValue"
              class="tip-add-btn"
              @click="addTipFromInput"
              title="Add tip">
              <b-icon icon="plus" size="is-small" />
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import db from "@/firebase/db"
import Draggable from "vuedraggable"
import Editor from "@/components/BaseEditor"
import AddNewPrepModal from "@/components/modals/AddNewPrepModal"
import AddNewUnitModal from "@/components/modals/AddNewUnitModal"
import AddNewIngredientModal from "@/components/modals/AddNewIngredientModal"

export default {
  name: 'EditIngredient',
  components: {
    Draggable,
    Editor
  },
  props: [
    "ingredientIn",
    "isNewIngredient"
  ],
  data() {
    return {
      ingredient: null,
      ingredients: [],
      preps: [],
      units: [],
      newTipValue: ''
    }
  },
  firestore: {
    ingredients: db.collection('ingredients').orderBy("name", "asc"),
    preps: db.collection('prep_terms').orderBy("term", "asc"),
    units: db.collection('ingredient_units').orderBy("term", "asc"),
  },
  created() {
    this.ingredient = this.ingredientIn ?? {id: null, name: "", amount: null, unit: "", prep: "", tips:[], optional: false};
    this._blurTimer = null;
  },
  computed: {
    filteredIngredients() {
      return this.ingredients.filter(option => {
        if (option.item_type && option.item_type !== 'ingredient') return false
        return option.name.toLowerCase().indexOf(this.ingredient.name.toLowerCase()) >= 0
      });
    },
    filteredPreps() {
      return this.preps.filter(option => {
        return option.term.toLowerCase().indexOf(this.ingredient.prep.toLowerCase()) >= 0
      });
    },
    filteredUnits() {
      return this.units.filter(option => {
        return option.term.toLowerCase().indexOf(this.ingredient.unit.toLowerCase()) >= 0
      });
    },
    hasIssues(){
      return this.ingredient.aisle == null || this.ingredient.ingredient_id == null;
    },
    containerClass(){
      return {
        'ingredient-item': true,
        'is-new': this.isNewIngredient,
        'is-error': this.hasIssues && !this.isNewIngredient
      };
    }
  },
  watch: {
    'ingredient.name'(newVal) {
      if (!this.ingredient.ingredient_id || !this.ingredients.length) return;
      const linked = this.ingredients.find(i => i.id === this.ingredient.ingredient_id);
      if (!linked || linked.name !== newVal) {
        this.$set(this.ingredient, 'aisle', null);
        this.$set(this.ingredient, 'ingredient_id', null);
      }
    }
  },
  methods: {
    onNameBlur() {
      this._blurTimer = setTimeout(() => {
        if (!this.isNewIngredient && this.ingredient.name && this.ingredient.aisle == null) {
          this.ingredient.name = '';
          this.$set(this.ingredient, 'ingredient_id', null);
          this.$emit('update-ingredient', this.ingredient);
          this.$buefy.toast.open({
            type: 'is-warning',
            message: 'Please select an ingredient from the dropdown, or use "+ Add new" to create one.',
            duration: 4000,
            position: 'is-top'
          });
        }
      }, 200);
    },
    selectedIngredient(option) {
      clearTimeout(this._blurTimer);
      if (option == null) {
        return;
      }
      this.ingredient.name = option.name;
      this.$set(this.ingredient, 'aisle', option.aisle);
      this.$set(this.ingredient, 'ingredient_id', option.id);
      this.$emit('update-ingredient', this.ingredient);
    },
    selectedPrep(option) {
      if (option == null) {
        return;
      }
      this.ingredient.prep = option.term;
      this.$emit('update-ingredient', this.ingredient);
    },
    selectedUnit(option) {
      if (option == null) {
        return;
      }
      this.ingredient.unit = option.term;
      this.$emit('update-ingredient', this.ingredient);
    },
    toggleOptional() {
      this.ingredient.optional = !this.ingredient.optional;
      this.update();
    },
    add() {
      if (this.ingredient.name && this.ingredient.aisle == null) {
        this.$buefy.toast.open({
          type: 'is-warning',
          message: 'Please select an ingredient from the dropdown before adding.',
          duration: 3500,
          position: 'is-top'
        });
        return;
      }
      this.ingredient.id = uuidv4();
      this.ingredient.unit = this.ingredient.unit ?? "";
      this.$emit('add-ingredient', this.ingredient);
      this.ingredient = {id: null, name: "", amount: null, unit: "", prep: "", tips: [], optional: false};
    },
    remove() {
      this.$emit('remove-ingredient', this.ingredient);
    },
    update() {
      this.$emit('update-ingredient', this.ingredient);
    },
    addTipFromInput() {
      if (this.newTipValue && this.newTipValue.trim()) {
        const newTip = {
          id: uuidv4(),
          value: this.newTipValue.trim()
        };
        if (!this.ingredient.tips) {
          this.ingredient.tips = [];
        }
        this.ingredient.tips.push(newTip);
        this.$emit('update-ingredient', this.ingredient);
        this.newTipValue = '';
      }
    },
    removeTip(_, index) {
      this.$buefy.dialog.confirm({
          title: 'Deleting Tip',
          message: `Are you sure you want to delete this tip?<br /><br /><strong>This action cannot be undone!</strong>`,
          confirmText: 'Delete Tip',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.$delete(this.ingredient.tips, index);
            this.$emit('update-ingredient', this.ingredient);
          }
      });
    },
    openNewIngredientModal(input) {
      let cleansedInput = input.toLowerCase().trim()
      this.$buefy.modal.open({
          parent: this,
          component: AddNewIngredientModal,
          hasModalCard: true,
          trapFocus: true,
          props: {
            input: cleansedInput
          },
          events: {
            'ingredient-added': value => {
              this.ingredient.name = value;
            }
          }
      })
    },
    openNewPrepModal(input) {
      let cleansedInput = input.toLowerCase().trim()
      this.$buefy.modal.open({
          parent: this,
          component: AddNewPrepModal,
          hasModalCard: true,
          trapFocus: true,
          props: {
            input: cleansedInput
          },
          events: {
            'prep-added': value => {
              this.ingredient.prep = value;
            }
          }
      })
    },
    openNewUnitModal(input) {
      let cleansedInput = input.toLowerCase().trim()
      this.$buefy.modal.open({
          parent: this,
          component: AddNewUnitModal,
          hasModalCard: true,
          trapFocus: true,
          props: {
            input: cleansedInput
          },
          events: {
            'unit-added': value => {
              this.ingredient.unit = value.term;
            }
          }
      })
    },
  }
}
</script>

<style scoped>
.ingredient-item {
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);
  transition: border-color 0.15s;
}

.ingredient-item:hover {
  border-color: var(--border-medium);
}

.ingredient-item.is-new {
  background: transparent;
  border: none;
}

.ingredient-item.is-error {
  border-color: #dc2626;
}

/* Header row with drag, optional toggle, delete */
.ingredient-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-light);
  background: rgba(0,0,0,0.02);
}

.ingredient-header-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.optional-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-muted);
  cursor: pointer;
}

.ingredient-drag {
  color: var(--text-muted);
  cursor: grab;
}

.ingredient-drag:active {
  cursor: grabbing;
}

/* Input fields row */
.ingredient-row {
  display: grid;
  grid-template-columns: 55px 75px 1fr 150px;
  gap: 2px;
  align-items: center;
  padding: 12px 14px;
}

.ingredient-qty :deep(.input),
.ingredient-unit :deep(.input),
.ingredient-name :deep(.input),
.ingredient-prep :deep(.input) {
  padding: 10px 12px !important;
  font-size: 13px !important;
  height: 40px !important;
}

.ingredient-qty :deep(.input) {
  text-align: center;
}

.ingredient-name :deep(.input) {
  font-weight: 600 !important;
}

.ingredient-toggle {
  width: 36px;
  height: 20px;
  background: var(--border-medium);
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;
}

.ingredient-toggle.active {
  background: var(--feeds-green);
}

.ingredient-toggle::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.ingredient-toggle.active::after {
  transform: translateX(16px);
}

.ingredient-action-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  flex-shrink: 0;
}

.ingredient-action-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.ingredient-action-btn.delete:hover {
  background: #fef2f2;
  color: #dc2626;
}

/* Tips Section */
.ingredient-tips {
  padding: 8px 14px 12px 14px;
}

.ingredient-tip-item {
  margin-bottom: 8px;
}

.ingredient-tip-item:last-child {
  margin-bottom: 0;
}

.ingredient-tip-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.ingredient-tip-wrapper.add-new {
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px dashed var(--border-light);
  border-radius: var(--radius-sm);
}

.ingredient-tip-wrapper.add-new:focus-within {
  border-style: solid;
  border-color: var(--feeds-orange);
  background: #fff8f5;
}

.ingredient-tip-icon {
  width: 18px;
  height: 18px;
  color: var(--feeds-orange);
  flex-shrink: 0;
}

.ingredient-tip-icon.tip-drag-handle {
  cursor: grab;
}

.ingredient-tip-icon.tip-drag-handle:active {
  cursor: grabbing;
}

.ingredient-tip-input {
  flex: 1;
  min-width: 0;
}

.ingredient-tip-input :deep(.ProseMirror) {
  border: none;
  padding: 0;
  min-height: 18px;
  font-size: 13px;
  background: transparent;
  box-shadow: none;
}

.ingredient-tip-input :deep(.ProseMirror:focus) {
  box-shadow: none;
  border: none;
}

.new-tip-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-primary);
}

.new-tip-input::placeholder {
  color: var(--text-muted);
  font-style: italic;
}

.tip-remove-btn,
.tip-add-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  flex-shrink: 0;
}

.tip-remove-btn:hover {
  background: #fef2f2;
  color: #dc2626;
}

.tip-add-btn:hover {
  background: var(--feeds-green-light);
  color: var(--feeds-green);
}

/* Add button styles */
.add-row-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: 2px dashed var(--border-medium);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.add-row-btn:hover {
  border-color: var(--feeds-green);
  color: var(--feeds-green);
  background: var(--feeds-green-light);
}

/* Remove number input spinners */
.ingredient-qty :deep(input::-webkit-outer-spin-button),
.ingredient-qty :deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.ingredient-qty :deep(input[type=number]) {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
